import { useCallback, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { FiltersTypes } from "@common/types/filters/general";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { YearFilter } from "@components/settings/YearFilter/YearFilter";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { PlainModal } from "@design-system/Modal/Modal";
import styles from "../ClassesSettings.module.scss";
import { TablePageHeader } from "../../../pageHeader/TablePageHeader";
import { CreateClassModal } from "./CreateClassModal";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";

interface Props {
    expanded: ExpandedState;
    tableRef: React.RefObject<ViewTableHandle | null>;
    search: string;
    setSearch: (search: string) => void;
    filters: Pick<FiltersTypes, "start" | "end" | "entityIds">;
    onFiltersChange: FiltersChangeFn;
}

export const ClassesSettingsHeader: React.FC<Props> = ({
    expanded,
    tableRef,
    search,
    setSearch,
    filters,
    onFiltersChange,
}) => {
    const [showAddClassModal, setShowAddClassModal] = useState(false);

    useKeyboardCommand({
        keys: "alt+n",
        callback: () => setShowAddClassModal(true),
        options: {
            description: "Add class",
            preventDefault: true,
        },
    });

    const onAddClassButtonClick = useCallback(() => {
        setShowAddClassModal(!showAddClassModal);
    }, [showAddClassModal]);

    return (
        <>
            <TablePageHeader
                search={search}
                onSearchChange={(value) => {
                    setSearch(value ?? "");
                }}
                isFullPage
            >
                <div className={styles.flexBox}>
                    <YearFilter filters={filters} onChange={onFiltersChange} />
                    <EntityFilterCombobox
                        filters={filters}
                        onChange={onFiltersChange}
                    />
                    <CollapseAllButton
                        expanded={expanded}
                        tableRef={tableRef}
                    />
                    <WithHotkey
                        hotKey="alt+n"
                        hotKeyOptions={{
                            description: "Create class",
                        }}
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onAddClassButtonClick}
                        >
                            Add class
                        </Button>
                    </WithHotkey>
                </div>
            </TablePageHeader>

            <PlainModal show={showAddClassModal} onHide={onAddClassButtonClick}>
                <CreateClassModal
                    parentClassId={null}
                    onHide={onAddClassButtonClick}
                />
            </PlainModal>
        </>
    );
};
